import Auth_API from "./apiConfig";

// Background BIC reads (page load, store hydration) handle failures in Redux —
// suppress the global axios toast so a CRM hiccup doesn't spam the marketing page.
const silent = { silent: true };

// --- Catalog (read-only, cached server-side) ---

export const getBicConfiguration = async () => {
  try {
    const response = await Auth_API.post("/bic/catalog/configuration", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicChapters = async () => {
  try {
    const response = await Auth_API.post("/bic/catalog/chapters", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicCities = async () => {
  try {
    const response = await Auth_API.post("/bic/catalog/cities", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicPods = async (chapterRefId) => {
  try {
    const response = await Auth_API.post("/bic/catalog/pods", chapterRefId ? { chapter_ref_id: chapterRefId } : {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicMyPod = async () => {
  try {
    const response = await Auth_API.post("/bic/catalog/pods/mine", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicMyChapter = async () => {
  try {
    const response = await Auth_API.post("/bic/catalog/chapter/mine", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicSessions = async ({ chapterRefId, dateFrom, dateTo, page, pageSize } = {}) => {
  try {
    const response = await Auth_API.post(
      "/bic/catalog/sessions",
      {
        ...(chapterRefId && { chapter_ref_id: chapterRefId }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        page: page || 1,
        page_size: pageSize || 20,
      },
      silent
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// --- Membership ---

export const getMyBicMembership = async () => {
  try {
    const response = await Auth_API.get("/bic/membership/me", silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const selectBicChapter = async (chapter) => {
  try {
    const response = await Auth_API.post("/bic/membership/chapter", { chapter });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// --- Payment (membership is a QLPay Recurring subscription, not a one-time order) ---

export const createBicPaymentLink = async (applyReferralDiscount) => {
  try {
    const response = await Auth_API.post(
      "/bic/payment/create-link",
      {
        apply_referral_discount: !!applyReferralDiscount,
        return_url: `${window.location.origin}/bic/pay/confirm`,
      },
      silent
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicPaymentStatus = async (refId) => {
  try {
    const response = await Auth_API.post("/bic/payment/status", { ref_id: refId }, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const cancelBicSubscription = async (refId) => {
  try {
    const response = await Auth_API.post("/bic/payment/cancel", { ref_id: refId });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// --- Sessions (one-time/Fixed payment — never subscription logic here) ---

export const registerForBicSession = async ({ sessionRefId, chapterRefId, useSessionCredit }) => {
  try {
    const response = await Auth_API.post("/bic/sessions/register", {
      session_ref_id: sessionRefId,
      ...(chapterRefId && { chapter_ref_id: chapterRefId }),
      // Only meaningful when the member's balance fully covers the charge (D8) — defaults to
      // true server-side, so this is only sent to let the member decline it.
      use_session_credit: useSessionCredit !== false,
      return_url: `${window.location.origin}/bic/sessions/pay/confirm`,
    });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

/** A member's BIC session credit wallet (D8) — balance in rupees + every lot ever issued. */
export const getBicSessionCreditBalance = async () => {
  try {
    const response = await Auth_API.post("/bic/sessions/credit/balance", {});
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicSessionPaymentStatus = async (refId) => {
  try {
    const response = await Auth_API.post("/bic/sessions/payment/status", { ref_id: refId });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getMyBicSessions = async () => {
  try {
    const response = await Auth_API.post("/bic/sessions/my", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// Attended-tab session content (sketch notes/summary/recording) — client_ref_id is resolved
// server-side from the auth token, only session_ref_id is sent.
export const getBicSessionContent = async ({ session_ref_id }) => {
  try {
    const response = await Auth_API.post("/bic/sessions/content", { session_ref_id });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// Missed tab — invited/registered/attended breakdown since the member joined.
export const getBicMissedSummary = async () => {
  try {
    const response = await Auth_API.post("/bic/sessions/my/missed-summary", {});
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const submitBicMemberQuery = async ({ session_ref_id, reason, details }) => {
  try {
    const response = await Auth_API.post("/bic/sessions/query", {
      ...(session_ref_id && { session_ref_id }),
      reason,
      details,
    });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// Read-only "Join now" status — whether the member can join right now and the resolved room
// join_url, if any. Never poll registerForBicSession's endpoint for this: that one mutates
// (auto-registers on first call), this one only reads.
export const getBicSessionJoinInfo = async (sessionRefId) => {
  try {
    const response = await Auth_API.post("/bic/sessions/join-info", { session_ref_id: sessionRefId }, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicSessionCreditRequest = async (sessionRefId) => {
  try {
    const response = await Auth_API.post(
      "/bic/sessions/credit-request/status",
      { session_ref_id: sessionRefId },
      silent
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const requestBicSessionCredit = async (sessionRefId) => {
  try {
    const response = await Auth_API.post("/bic/sessions/credit", { session_ref_id: sessionRefId });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// --- Referrals (through qloneapi, NOT the standalone referralApiConfig CRM client) ---

export const submitBicReferral = async ({ chapterRefId, name, phone, email, consent }) => {
  try {
    const response = await Auth_API.post("/bic/referral/submit", {
      chapter_ref_id: chapterRefId,
      name,
      phone,
      email,
      consent: !!consent,
    });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const checkBicReferralDuplicate = async (phone) => {
  try {
    const response = await Auth_API.post("/bic/referral/duplicate-check", { phone });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    // Fail open — a flaky pre-check must never block a referral the member is entitled to submit.
    return { status: true, response: { is_duplicate: false } };
  }
};

export const listBicReferrals = async ({ status, chapterRefId, page, limit } = {}) => {
  try {
    const response = await Auth_API.post(
      "/bic/referral/list",
      {
        ...(status && { status }),
        ...(chapterRefId && { chapter_ref_id: chapterRefId }),
        page: page || 1,
        limit: limit || 20,
      },
      silent
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const getBicReferralCouponBalance = async () => {
  try {
    const response = await Auth_API.post("/bic/referral/coupon-balance", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const submitCrossChapterReferral = async ({ name, phone, email, city, details, consent }) => {
  try {
    const response = await Auth_API.post("/bic/referral/refer-other-chapter", {
      name,
      phone,
      email,
      city,
      details,
      consent: !!consent,
    });
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const listCrossChapterReferrals = async ({ page, limit } = {}) => {
  try {
    const response = await Auth_API.post(
      "/bic/referral/refer-other-chapter/list",
      { page: page || 1, limit: limit || 20 },
      silent
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// --- KYC ---
// The member's own KYC (`kyc` null until submitted) plus `prefill` from their QL One profile.
export const getBicKyc = async () => {
  try {
    const response = await Auth_API.post("/bic/kyc/mine", {}, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

// First submit locks the form; a resubmit only works after the BIC team approves an edit request.
export const submitBicKyc = async (form) => {
  try {
    const response = await Auth_API.post("/bic/kyc/submit", form, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};

export const requestBicKycEdit = async (reason) => {
  try {
    const response = await Auth_API.post("/bic/kyc/edit-request", { reason }, silent);
    return response.data;
  } catch (error) {
    console.error("Error:", error.message);
    throw new Error("An unexpected error occurred. Please try again.");
  }
};
